import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`These guidelines summarize how GitHub implements buttons across our products. We have standardized buttons documented on `}<a parentName="p" {...{
        "href": "https://primer.style/css/"
      }}>{`Primer CSS`}</a>{` and `}<a parentName="p" {...{
        "href": "https://primer.style/components"
      }}>{`Primer Components`}</a>{`, and these docs which serve as the source of truth for development implementation. This article serves to supplement our technical docs with proper guidance on design implementation.`}</p>
    <h2>{`Types`}</h2>
    <p>{`At GitHub, buttons are a fundamental building block of our products. Most of the time, we use the "Default" button type, but other types of buttons may be used to indicate something special about the button's hierarchy or functionality. The following table catalogues a quick glance at how we use buttons at GitHub:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Visual`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Default`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><img parentName="td" {...{
              "src": "https://user-images.githubusercontent.com/2313998/130091137-4f65a6cd-a890-434d-8ec7-292417f149af.png",
              "alt": "btn"
            }}></img></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The go-to style to render a button`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Outline`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><img parentName="td" {...{
              "src": "https://user-images.githubusercontent.com/2313998/130091138-3e548b4a-0f31-45fb-beed-70aaf7c6b4f2.png",
              "alt": "btn-outline"
            }}></img></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To downplay a button, for navigation buttons, or for filter buttons`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Primary`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><img parentName="td" {...{
              "src": "https://user-images.githubusercontent.com/2313998/129806123-d7001d50-2900-435e-bb6b-f8289de1a415.png",
              "alt": "btn-primary"
            }}></img></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To emphasize the highest priority action on a view`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Danger`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><img parentName="td" {...{
              "src": "https://user-images.githubusercontent.com/2313998/130091133-a48deda5-089a-4e1d-ac6b-a058a5c2497f.png",
              "alt": "btn-danger"
            }}></img></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`To warn that an action is potentially dangerous or destructive`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Invisible`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><img parentName="td" {...{
              "src": "https://user-images.githubusercontent.com/2313998/129628226-bbb0aae4-8cba-4175-aa0f-33a6213f4f67.png",
              "alt": "btn-invisible"
            }}></img></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A subtle style used for actions that are of lower priority in a view's button hierarchy`}</td>
        </tr>
      </tbody>
    </table>
    <h2>{`Text`}</h2>
    <h3>{`Labels`}</h3>
    <p>{`When designing buttons, keep the label as concise as possible. Buttons are to be understood by their type and text. If further clarity is required, consider adding an Octicon or adding a caption.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/24916540/52501276-d3330080-2b94-11e9-954e-38c0f09f8ca1.png" />
    <Caption mdxType="Caption">Use sentence case</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/24916540/52501272-d3330080-2b94-11e9-8829-7d12aae36ba1.png" />
    <Caption mdxType="Caption">Don't use all-caps or other text formats</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Captions`}</h3>
    <p>{`In certain scenarios, buttons require supporting text to give the user more context. Use text labels sparingly, as buttons should generally be able to be understood on their own.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/24916540/52501400-1b522300-2b95-11e9-8c47-71af68cfaeec.png" />
    <Caption mdxType="Caption">Keep text brief, specific, and descriptive</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/24916540/53110379-cbfee180-3508-11e9-8762-04e3c8979afb.png" />
    <Caption mdxType="Caption">Don't use unnecessary, redundant text descriptions</Caption>
  </Dont>
    </DoDontContainer>
    <h2>{`Sizes`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Size`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Medium`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The default button size`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Small`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Decrease button size when space is limited and the action is of lesser significance.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Large`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Increase button size to bring prominence to buttons that have major impact. Use large buttons sparingly, as layouts should generally have only one large button.`}</td>
        </tr>
      </tbody>
    </table>
    <h2>{`Alignments`}</h2>
    <h3>{`Order`}</h3>
    <p>{`When a secondary button is placed in conjunction with a primary button, the primary button should always be placed on the outermost alignment, or on top and extended full-width if stacked.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/2313998/129807268-f6db63e7-c3c8-48e3-844f-a68daa7f52a1.png" />
    <Caption mdxType="Caption">Follow this principle to align your buttons accordingly</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/2313998/129807410-984487b7-4088-4a12-bdac-07e1123b68dc.png" />
    <Caption mdxType="Caption">Don't ignore this principle and misalign primary buttons</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Full-width`}</h3>
    <p>{`In certain scenarios, it may make sense to use full-width buttons that extend to the width of their container. Use this style sparingly, but some appropriate use-cases include:`}</p>
    <ul>
      <li parentName="ul">{`Modals with a singular call-to-action`}</li>
      <li parentName="ul">{`Responsive behavior of buttons when scaled down`}</li>
    </ul>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/2313998/129805857-a554da0c-38d5-48da-b717-a4fa843d3aba.png" />
    <Caption mdxType="Caption">Use this pattern to confirm important actions</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/2313998/129805859-da4774ab-34ee-4d65-98b4-801707b7875c.png" />
    <Caption mdxType="Caption">Don't misuse this modifier by extending buttons full-width unnecessarily</Caption>
  </Dont>
    </DoDontContainer>
    <h2>{`Icons`}</h2>
    <p>{`Use `}<a parentName="p" {...{
        "href": "https://primer.style/octicons/"
      }}>{`Octicons`}</a>{` in conjunction with buttons to clarify an action if text alone is not enough to explain its functionality.`}</p>
    <p>{`In some cases, Octicons are used to create an identifiable visual indicator for a common action or paradigm. For example, the repo icon is used next to repo links, the new repo button, and repeated in the new repo flow. This repetition and continuity helps people learn and identify our visual language.`}</p>
    <h3>{`Icon with label`}</h3>
    <p>{`Most of the time, a button will have an icon and a label for clarity.`}</p>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/129626583-22dce7ca-611f-42c7-9a51-2ba1551bb5f5.png",
        "alt": "button with label and icon"
      }}></img></p>
    <h3>{`Icon only`}</h3>
    <p>{`In some cases, an action can be understood with using just an icon. Icon-only buttons can help save space in a cramped area.`}</p>
    <p>{`Buttons that only use an icon should still have a label that is invisible to sighted users, but accessible to assistive technology (for example: read aloud by a screenreader).`}</p>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/129626586-378e2b7f-7300-4804-93fb-c88f7f22407e.png",
        "alt": "button with icon but no label"
      }}></img></p>
    <h2>{`Counters`}</h2>
    <p>{`Counters can be added in appropriate scenarios to provide context. For example, showing social proof by displaying the number of people watching a repo.`}</p>
    <h4>{`Counter inside button`}</h4>
    <p>{`A count can be displayed inside of a button using a `}<a parentName="p" {...{
        "href": "https://primer.style/react/CounterLabel"
      }}>{`CounterLabel`}</a>{`.`}</p>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/129626580-465111c8-9237-4e33-af42-b2e830ea9cc3.png",
        "alt": "button with count inside"
      }}></img></p>
    <h4>{`Counter attached to a button`}</h4>
    <p>{`A count can be displayed as a separate element that is attached to a button. Only display a count as separate element that is attached to a button when the button uses the "small" size variant and the button type is "Default" or "Outline".`}</p>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/129626579-a2f17327-1b6e-43b4-8971-5b9a24f96dda.png",
        "alt": "button with count attached"
      }}></img></p>
    <h2>{`Dropdown triggers`}</h2>
    <h3>{`Button that toggles a dropdown`}</h3>
    <p>{`When a button is used to toggle a dropdown, it should have a visual cue to help users understand the functionality.`}</p>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/129630903-d66a1ec8-51b5-4ed8-9a28-8ef90518b1c0.png",
        "alt": "dropdown button example"
      }}></img></p>
    <h3>{`Button with action variants`}</h3>
    <p>{`Group a button with a dropdown button in instances where a primary action allows for variants on the primary action. For example, there are different merge strategies a user can choose from when merging a pull request.`}</p>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/129630910-8c21b316-e6d6-4fb9-b7c2-2d21283a4e86.png",
        "alt": "button with action variants example"
      }}></img></p>
    <h2>{`Groups`}</h2>
    <p>{`Use button groups to organize similar functionality. Only "Default" and "Outline" button types can be used in button groups. This modifier can also be used as a filtering pattern.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/2313998/129626573-125289b2-c286-4d64-a228-905661ed1445.png" />
    <Caption mdxType="Caption">Group buttons with closely related functionality</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/2313998/129626578-a42786bf-9ff7-48a8-b755-d6e52541dd3d.png" />
    <Caption mdxType="Caption">Group buttons just because they're close together</Caption>
  </Dont>
    </DoDontContainer>
    <h2>{`Best practices`}</h2>
    <h3>{`Limit primary button usage`}</h3>
    <p>{`Only use one primary button on the page, whenever possible, to indicate its emphasis relative to other actions. Primary buttons have top priority in visual hierarchy, and using too many of them on a single view dilutes their effectiveness.`}</p>
    <h3>{`Use outline buttons independently`}</h3>
    <p>{`Outline buttons are mainly used for secondary actions such as filtering. Don't use in conjunction or place in close proximity of other button styles`}</p>
    <h2>{`Support`}</h2>
    <h3>{`Requesting feedback`}</h3>
    <p>{`If you have any questions while implementing buttons, or are looking for feedback, please reach out to the `}<a parentName="p" {...{
        "href": "https://github.slack.com/archives/CSGAVNZ19"
      }}>{`#primer`}</a>{` channel on Slack.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      